import {
  ArrowTrendingUpIcon,
  ChatBubbleOvalLeftIcon,
  EllipsisHorizontalIcon,
  MegaphoneIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useSyncExternalStore } from 'react';
import { twMerge } from 'tailwind-merge';

import { MoreSheet, useMoreSheetOpen } from '@/components/Nav/MoreSheet';
import { usePublishedPendingCount } from '@/components/Nav/PendingCountBadge';
import { getBadgeSource, subscribeBadgeSources } from '@/renderer/utils/appBadge';
import { useIsomorphicLayoutEffect } from '@/renderer/utils/hooks/useIsomorphicLayoutEffect';
import { useKeyboardOpen } from '@/renderer/utils/hooks/useKeyboardOpen';
import { isIosNative } from '@/renderer/utils/isIosNative';
import {
  closeMoreSheet,
  dismissMoreSheet,
  moreSheetOpenedOn,
  openMoreSheet,
  registerTabBar,
} from '@/renderer/utils/moreSheet';

/**
 * The mobile primary navigation: a five-tab bar docked to the bottom edge.
 *
 * ## It is the sidebar, not a second navigation
 *
 * This renders from inside renderer/Nav.tsx, AFTER both of Nav's "should this
 * page have navigation at all" gates (HIDE_ON, MARKETING_LANDING_ROUTES). That
 * placement is the point: the tab bar can only appear where the sidebar
 * appears, and can only disappear where the sidebar disappears, so the two can
 * never drift into the state issue #1405 describes — a signed-in user on a
 * guarded route with no way out. Do not lift this to PageLayout or give it
 * route rules of its own.
 *
 * Above `md` it is hidden outright and the desktop sidebar is unchanged. Below
 * `md` it is the ONLY primary navigation (#2142): there is no header hamburger
 * and no phone drawer. The tabs are Release, Results, Order (Admin for staff,
 * who find Order in More instead) and Chat; everything else lives in the More
 * sheet (components/Nav/MoreSheet.tsx) the fifth tab opens over the current
 * page. The More tab is still an <a href="/more"> so it works before hydration;
 * /more itself opens the sheet over the release center.
 *
 * Order lands on /order everywhere; that page itself branches on
 * isIosNative() to the In-App Purchase surface (#2100), so the tab carries no
 * platform logic of its own.
 *
 * ## Docked, one treatment
 *
 * Full width, flush with the bottom edge, a hairline top rule, and the bottom
 * safe-area inset as padding beneath the tabs so the home indicator sits under
 * the bar rather than on a tab. Translucent with a backdrop blur on every
 * platform; it replaced a floating capsule that covered the end of every page
 * (#2142). Static markup, so SSR and hydration agree on every platform.
 *
 * ## Layout clearance
 *
 * The bar is `position: fixed`, so nothing reserves room for it on its own. It
 * publishes its footprint (bar + the bottom safe-area inset) as
 * `--tabbar-height` on <html>, plus a `data-tabbar` marker; renderer/styles/
 * index.css derives `--tabbar-space` from those — 0 above `md` and 0 when no bar
 * is mounted — and reserves it once, at the end of <main>, for every page.
 * Surfaces that size themselves to the viewport instead of flowing inside
 * <main> subtract the same variable.
 *
 * ## The keyboard
 *
 * A bottom-anchored bar and the on-screen keyboard want the same strip of
 * screen. The bar yields: while the keyboard is up it translates off the bottom
 * edge, so it neither sits on top of the keyboard nor fights the release-center
 * composer or the support-chat message box for the space above it.
 *
 * On Android and the web its footprint also drops to 0, so the layout stops
 * reserving room the bar is not using. On native iOS the footprint is kept and
 * the layout's `--bottom-inset` (the taller of the bar and the eased keyboard
 * inset, renderer/styles/index.css) hands over from one to the other: zeroing
 * it while the keyboard eased in dropped the content before it rose (#2142).
 * There the bar also leaves in the SAME frame keyboardWillShow arrives, with
 * no slide: sliding out for 200ms left its frosted strip showing around the
 * rising keyboard. It still slides back in on the way down.
 */

// The tab row's height (h-14) plus its 1px top hairline, above the safe-area
// padding. Kept here because the layout clearance below is computed from it.
const BAR_HEIGHT = 57;

/**
 * The key the release-center unread counter registers under (the sidebar's
 * Release Center link passes `dataPath: '/release-center'`), read the same way
 * as the chat badge below.
 */
const RELEASE_BADGE_KEY = '/release-center';

/**
 * The key the support-chat unread counter registers under in the shared badge
 * aggregator — WebSocketUnreadCounter keys every source by its `dataPath`, and
 * the sidebar's Support Chat link passes `dataPath: '/support-chat'`. Reading
 * that source is what makes this badge the SAME number the sidebar badge shows
 * (and the same number the OS app-icon badge is summed from) instead of a second
 * count with its own websocket subscription and its own idle/presence handling.
 * See renderer/utils/appBadge.ts.
 */
const CHAT_BADGE_KEY = '/support-chat';

type Tab = {
  href: string;
  /** Prefix match for the active state, when the destination has sub-routes. */
  startsWith?: string;
  icon: typeof MegaphoneIcon;
  label: string;
  badge?: 'release' | 'pending' | 'chat';
  /** Opens the More sheet instead of navigating. */
  sheet?: boolean;
};

function tabsFor(signedIn: boolean, isStaff: boolean): Tab[] {
  return [
    {
      href: '/release-center',
      icon: MegaphoneIcon,
      label: 'Release',
      badge: 'release',
    },
    // Mirrors the sidebar's single "Results" entry (#2004): members go to their
    // own results, signed-out visitors to the public archive.
    signedIn
      ? {
          href: '/results',
          startsWith: '/results',
          icon: ArrowTrendingUpIcon,
          label: 'Results',
          badge: 'pending',
        }
      : { href: '/archive', startsWith: '/archive', icon: ArrowTrendingUpIcon, label: 'Results' },
    // Staff live in the admin tools, so Admin takes the third slot for them
    // (same role check as the sidebar's Admin link) and Order moves into More.
    isStaff
      ? { href: '/admin', startsWith: '/admin', icon: WrenchScrewdriverIcon, label: 'Admin' }
      : { href: '/order', startsWith: '/order', icon: ShoppingBagIcon, label: 'Order' },
    {
      href: '/support-chat',
      startsWith: '/support-chat',
      icon: ChatBubbleOvalLeftIcon,
      label: 'Chat',
      badge: 'chat',
    },
    {
      href: '/more',
      icon: EllipsisHorizontalIcon,
      label: 'More',
      sheet: true,
    },
  ];
}

function useBadgeSource(key: string): number {
  return useSyncExternalStore(
    subscribeBadgeSources,
    () => getBadgeSource(key),
    () => 0,
  );
}

export function MobileTabBar({
  urlPathname,
  signedIn,
  isStaff = false,
}: { urlPathname: string; signedIn: boolean; isStaff?: boolean }) {
  const keyboardOpen = useKeyboardOpen();
  // Only read while the keyboard is up, which it never is during SSR or
  // hydration, so the server and client markup still agree.
  const iosNative = keyboardOpen && isIosNative();
  const sheetOpen = useMoreSheetOpen();
  const badges = {
    release: useBadgeSource(RELEASE_BADGE_KEY),
    pending: usePublishedPendingCount(),
    chat: useBadgeSource(CHAT_BADGE_KEY),
  };

  const footprint =
    keyboardOpen && !iosNative ? '0px' : `calc(var(--safe-bottom) + ${BAR_HEIGHT}px)`;

  useIsomorphicLayoutEffect(() => {
    const root = document.documentElement;

    root.setAttribute('data-tabbar', '');
    root.style.setProperty('--tabbar-height', footprint);

    return () => {
      root.removeAttribute('data-tabbar');
      root.style.removeProperty('--tabbar-height');
    };
  }, [footprint]);

  // The sheet only exists while a tab bar does (renderer/utils/moreSheet.ts).
  useEffect(() => registerTabBar(), []);

  // The page changed under an open sheet without going through it (e.g. a
  // forced sign-out redirect): drop it rather than float it over the new page.
  useEffect(() => {
    const openedOn = moreSheetOpenedOn();
    if (openedOn && openedOn !== urlPathname) {
      dismissMoreSheet();
    }
  }, [urlPathname]);

  const tabs = tabsFor(signedIn, isStaff);

  return (
    <>
      <nav
        id="ras-tabbar"
        aria-label="Primary"
        aria-hidden={keyboardOpen || undefined}
        className={twMerge(
          // z-30 is deliberate: the release-center compose sheet and its scrim
          // (and the More sheet) have to cover it. It still sits above ordinary
          // page content. --safe-bottom, never a bare env(): on Android
          // env(safe-area-inset-bottom) reports only the display cutout, so the
          // tabs would sit on the gesture bar (#2092/#2093).
          'fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 items-stretch border-zinc-900/10 border-t bg-white/80 pb-[var(--safe-bottom)] backdrop-blur-xl backdrop-saturate-150 transition-transform duration-200 ease-out md:hidden dark:border-white/10 dark:bg-zinc-900/80',
          // Out of the keyboard's way. Translated fully off the bottom edge
          // rather than unmounted, so the bar animates back to where it was.
          keyboardOpen && 'pointer-events-none translate-y-full',
          keyboardOpen && iosNative && 'invisible transition-none',
        )}
      >
        {tabs.map((tab) => (
          <TabLink
            key={tab.href}
            tab={tab}
            active={
              tab.sheet
                ? sheetOpen
                : !sheetOpen &&
                  (urlPathname === tab.href ||
                    (!!tab.startsWith && urlPathname.startsWith(tab.startsWith)))
            }
            sheetOpen={sheetOpen}
            badge={tab.badge ? badges[tab.badge] : 0}
            // Pending plays are not "unread"; label the count for what it is.
            badgeLabel={tab.badge === 'pending' ? 'pending' : 'unread'}
          />
        ))}
      </nav>
      <MoreSheet />
    </>
  );
}

// No `hover:` anywhere: Tailwind v4 gates it behind `(hover: hover)`, which
// never matches on the touch devices this bar exists for, so a hover-only
// affordance would simply be unreachable (#1468). Press feedback is `active:`.
// Each tab is the full 56px row and its whole column is tappable,
// not just the icon.
const tabClass =
  'relative flex h-14 select-none flex-col items-center justify-center gap-1 text-zinc-600 no-underline transition-opacity active:opacity-60 dark:text-zinc-400';

const activeTabClass = 'text-[#15734A] dark:text-[#34B473]';

function TabLink({
  tab,
  active,
  sheetOpen,
  badge,
  badgeLabel,
}: { tab: Tab; active: boolean; sheetOpen: boolean; badge: number; badgeLabel: string }) {
  const Icon = tab.icon;

  return (
    <a
      href={tab.href}
      data-active={active || undefined}
      aria-current={active && !tab.sheet ? 'page' : undefined}
      // The More tab is a link to /more only until hydration; after that it
      // opens (or closes) the sheet in place. data-vike="false" keeps Vike's
      // document-level link interception (which ignores preventDefault) from
      // also navigating to /more.
      data-vike={tab.sheet ? 'false' : undefined}
      aria-haspopup={tab.sheet ? 'dialog' : undefined}
      aria-expanded={tab.sheet ? sheetOpen : undefined}
      onClick={
        tab.sheet
          ? (event) => {
              event.preventDefault();
              if (sheetOpen) {
                closeMoreSheet();
              } else {
                openMoreSheet();
              }
            }
          : undefined
      }
      className={twMerge(tabClass, active && activeTabClass)}
    >
      <Icon className="size-[23px] shrink-0" aria-hidden="true" />
      <span
        className={twMerge(
          'font-medium text-[10px] leading-none tracking-tight',
          active && 'font-semibold',
        )}
      >
        {tab.label}
      </span>
      {badge > 0 && (
        <span
          aria-label={`${badge} ${badgeLabel}`}
          className={twMerge(
            'absolute top-1 right-[calc(50%-22px)] inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 font-bold text-[10px] text-white leading-none',
            // Same amber the sidebar's pending badge uses: a count of plays to
            // watch, not something unread.
            badgeLabel === 'pending' && 'bg-amber-400 text-zinc-900',
          )}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </a>
  );
}

export default MobileTabBar;
